import { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Music, Video, Youtube, Link2, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

export type MediaInsertType = 'image-url' | 'audio-url' | 'video-url' | 'video-embed';

interface MediaInsertModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: MediaInsertType;
  onInsert: (html: string) => void;
}

// Parse YouTube URL and extract video ID
const parseYouTubeUrl = (url: string): string | null => {
  // Normalize URL - handle with or without www, http/https
  const normalizedUrl = url.trim();

  // Handle various YouTube URL formats
  const patterns = [
    // Standard watch URL: youtube.com/watch?v=VIDEO_ID (handles www. and extra params)
    /(?:www\.)?youtube\.com\/watch\?(?:[^&]*&)*v=([a-zA-Z0-9_-]{11})/i,
    // Short URL: youtu.be/VIDEO_ID
    /youtu\.be\/([a-zA-Z0-9_-]{11})/i,
    // Embed URL: youtube.com/embed/VIDEO_ID
    /(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/i,
    // Shorts URL: youtube.com/shorts/VIDEO_ID
    /(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i,
    // Live URL: youtube.com/live/VIDEO_ID
    /(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/i,
    // Nocookie embed: youtube-nocookie.com/embed/VIDEO_ID
    /(?:www\.)?youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]{11})/i,
    // Simple v= pattern as fallback (catches most edge cases)
    /[?&]v=([a-zA-Z0-9_-]{11})(?:&|$)/i,
  ];

  for (const pattern of patterns) {
    const match = normalizedUrl.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

// Parse Vimeo URL and extract video ID
const parseVimeoUrl = (url: string): string | null => {
  const patterns = [
    /vimeo\.com\/(\d+)/,
    /vimeo\.com\/video\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Validate URL format
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Check if URL is a valid media URL based on type
const validateMediaUrl = (url: string, type: MediaInsertType): { valid: boolean; message: string } => {
  if (!url.trim()) {
    return { valid: false, message: 'Please enter a URL' };
  }

  if (!isValidUrl(url)) {
    return { valid: false, message: 'Please enter a valid URL' };
  }

  switch (type) {
    case 'image-url': {
      const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;
      const isImageUrl = imageExtensions.test(url) || url.includes('images') || url.includes('img');
      return {
        valid: true, // Allow any URL, user might have a valid image URL without extension
        message: isImageUrl ? 'Valid image URL' : 'URL accepted - make sure it points to an image',
      };
    }
    case 'audio-url': {
      const audioExtensions = /\.(mp3|wav|ogg|m4a|aac|flac|webm)(\?.*)?$/i;
      const isAudioUrl = audioExtensions.test(url) || url.includes('audio');
      return {
        valid: true,
        message: isAudioUrl ? 'Valid audio URL' : 'URL accepted - make sure it points to an audio file',
      };
    }
    case 'video-url': {
      const videoExtensions = /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i;
      const isVideoUrl = videoExtensions.test(url) || url.includes('video');
      return {
        valid: true,
        message: isVideoUrl ? 'Valid video URL' : 'URL accepted - make sure it points to a video file',
      };
    }
    case 'video-embed': {
      const youtubeId = parseYouTubeUrl(url);
      const vimeoId = parseVimeoUrl(url);
      if (youtubeId) {
        return { valid: true, message: 'YouTube video detected' };
      }
      if (vimeoId) {
        return { valid: true, message: 'Vimeo video detected' };
      }
      return { valid: false, message: 'Please enter a valid YouTube or Vimeo URL' };
    }
    default:
      return { valid: false, message: 'Unknown media type' };
  }
};

export default function MediaInsertModal({ isOpen, onClose, type, onInsert }: MediaInsertModalProps) {
  const [url, setUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [validation, setValidation] = useState<{ valid: boolean; message: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // Reset state when modal opens/closes or type changes
  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setAltText('');
      setValidation(null);
      setPreviewLoading(false);
      setPreviewError(false);
    }
  }, [isOpen, type]);

  // Validate URL on change (debounced)
  useEffect(() => {
    if (!url.trim()) {
      setValidation(null);
      return;
    }

    const timer = setTimeout(() => {
      setValidation(validateMediaUrl(url, type));
      setPreviewError(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [url, type]);

  if (!isOpen) return null;

  const getModalConfig = () => {
    switch (type) {
      case 'image-url':
        return {
          title: 'Insert Image from URL',
          icon: <ImageIcon className="w-5 h-5" />,
          color: 'blue',
          placeholder: 'https://example.com/image.jpg',
          showAltText: true,
        };
      case 'audio-url':
        return {
          title: 'Insert Audio from URL',
          icon: <Music className="w-5 h-5" />,
          color: 'green',
          placeholder: 'https://example.com/audio.mp3',
          showAltText: false,
        };
      case 'video-url':
        return {
          title: 'Insert Video from URL',
          icon: <Video className="w-5 h-5" />,
          color: 'purple',
          placeholder: 'https://example.com/video.mp4',
          showAltText: false,
        };
      case 'video-embed':
        return {
          title: 'Embed YouTube / Vimeo Video',
          icon: <Youtube className="w-5 h-5" />,
          color: 'red',
          placeholder: 'https://www.youtube.com/watch?v=...',
          showAltText: false,
        };
      default:
        return {
          title: 'Insert Media',
          icon: <Link2 className="w-5 h-5" />,
          color: 'gray',
          placeholder: 'https://...',
          showAltText: false,
        };
    }
  };

  const config = getModalConfig();

  const handleInsert = () => {
    if (!validation?.valid) return;

    let html = '';

    switch (type) {
      case 'image-url':
        html = `<p style="text-align: center; margin: 20px auto;">
  <img
    src="${url}"
    alt="${altText || 'Image'}"
    class="content-image"
    style="max-width: 100%; height: auto; display: block; margin: 0 auto; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
    loading="lazy"
  />
</p>`;
        break;

      case 'audio-url':
        html = `<div style="margin: 20px auto; max-width: 100%;">
  <audio
    controls
    controlslist="nodownload"
    preload="metadata"
    style="width: 100%; border-radius: 8px;"
  >
    <source src="${url}" type="audio/mpeg" />
    Your browser does not support the audio element.
  </audio>
</div>`;
        break;

      case 'video-url':
        html = `<div style="margin: 20px auto; max-width: 100%;">
  <video
    controls
    controlslist="nodownload"
    disablepictureinpicture
    preload="metadata"
    style="width: 100%; max-width: 800px; display: block; margin: 0 auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);"
  >
    <source src="${url}" type="video/mp4" />
    Your browser does not support the video element.
  </video>
</div>`;
        break;

      case 'video-embed': {
        const youtubeId = parseYouTubeUrl(url);
        const vimeoId = parseVimeoUrl(url);

        if (youtubeId) {
          html = `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 20px auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
  <iframe
    src="https://www.youtube.com/embed/${youtubeId}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; border-radius: 8px;"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowfullscreen
    title="YouTube video"
  ></iframe>
</div>`;
        } else if (vimeoId) {
          html = `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 20px auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
  <iframe
    src="https://player.vimeo.com/video/${vimeoId}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; border-radius: 8px;"
    allow="autoplay; fullscreen; picture-in-picture"
    allowfullscreen
    title="Vimeo video"
  ></iframe>
</div>`;
        }
        break;
      }
    }

    if (html) {
      onInsert(html);
      onClose();
    }
  };

  const renderPreview = () => {
    if (!url.trim() || !validation?.valid) return null;

    switch (type) {
      case 'image-url':
        return (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs font-medium text-gray-500 mb-2">Preview:</div>
            {previewLoading && (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}
            {previewError ? (
              <div className="flex items-center justify-center h-32 text-gray-400">
                <span>Unable to load preview</span>
              </div>
            ) : (
              <img
                src={url}
                alt={altText || 'Preview'}
                className="max-h-48 max-w-full mx-auto rounded"
                onLoad={() => setPreviewLoading(false)}
                onError={() => {
                  setPreviewLoading(false);
                  setPreviewError(true);
                }}
                style={{ display: previewLoading ? 'none' : 'block' }}
              />
            )}
          </div>
        );

      case 'audio-url':
        return (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs font-medium text-gray-500 mb-2">Preview:</div>
            <audio
              controls
              controlsList="nodownload"
              preload="metadata"
              className="w-full"
              onError={() => setPreviewError(true)}
            >
              <source src={url} />
            </audio>
            {previewError && (
              <div className="text-xs text-amber-600 mt-1">
                Unable to preview - URL may still work when inserted
              </div>
            )}
          </div>
        );

      case 'video-url':
        return (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs font-medium text-gray-500 mb-2">Preview:</div>
            <video
              controls
              controlsList="nodownload"
              disablePictureInPicture
              preload="metadata"
              className="max-h-48 max-w-full mx-auto rounded"
              onError={() => setPreviewError(true)}
            >
              <source src={url} />
            </video>
            {previewError && (
              <div className="text-xs text-amber-600 mt-1">
                Unable to preview - URL may still work when inserted
              </div>
            )}
          </div>
        );

      case 'video-embed': {
        const youtubeId = parseYouTubeUrl(url);
        const vimeoId = parseVimeoUrl(url);

        if (youtubeId) {
          return (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-xs font-medium text-gray-500 mb-2">Preview (YouTube):</div>
              <div className="aspect-video max-w-md mx-auto">
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  className="w-full h-full rounded"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video preview"
                />
              </div>
            </div>
          );
        }

        if (vimeoId) {
          return (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-xs font-medium text-gray-500 mb-2">Preview (Vimeo):</div>
              <div className="aspect-video max-w-md mx-auto">
                <iframe
                  src={`https://player.vimeo.com/video/${vimeoId}`}
                  className="w-full h-full rounded"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Vimeo video preview"
                />
              </div>
            </div>
          );
        }

        return null;
      }

      default:
        return null;
    }
  };

  const colorClasses: Record<string, { bg: string; hover: string; ring: string }> = {
    blue: { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', ring: 'focus:ring-blue-500' },
    green: { bg: 'bg-green-500', hover: 'hover:bg-green-600', ring: 'focus:ring-green-500' },
    purple: { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', ring: 'focus:ring-purple-500' },
    red: { bg: 'bg-red-500', hover: 'hover:bg-red-600', ring: 'focus:ring-red-500' },
    gray: { bg: 'bg-gray-500', hover: 'hover:bg-gray-600', ring: 'focus:ring-gray-500' },
  };

  const colors = colorClasses[config.color] || colorClasses.gray;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${colors.bg} text-white rounded-t-xl`}>
          <div className="flex items-center gap-2">
            {config.icon}
            <h3 className="font-semibold">{config.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* URL Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={config.placeholder}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${colors.ring} ${
                validation && !validation.valid ? 'border-red-300' : 'border-gray-300'
              }`}
              autoFocus
            />
            {validation && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${validation.valid ? 'text-green-600' : 'text-red-600'}`}>
                {validation.valid ? (
                  <CheckCircle className="w-3 h-3" />
                ) : (
                  <AlertCircle className="w-3 h-3" />
                )}
                {validation.message}
              </div>
            )}
          </div>

          {/* Alt Text (for images) */}
          {config.showAltText && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alt Text <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Description of the image"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Alt text helps with accessibility and SEO
              </p>
            </div>
          )}

          {/* Embed Help Text */}
          {type === 'video-embed' && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-600">
                <strong>Supported formats:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>YouTube: youtube.com/watch?v=..., youtu.be/...</li>
                  <li>Vimeo: vimeo.com/123456789</li>
                </ul>
              </div>
            </div>
          )}

          {/* Preview */}
          {renderPreview()}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInsert}
            disabled={!validation?.valid}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${colors.bg} ${colors.hover} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}
